import React, { useEffect, useState } from 'react';
import { featureAPI } from '../../../api/services';
import { useAppSelector } from '../../../store/hooks';

interface Props {
  projectId?: string;
}

const priorityColor: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

const FeatureApprovalQueue: React.FC<Props> = ({ projectId }) => {
  const { user } = useAppSelector(state => state.auth);
  const canApprove = user?.role === 'admin' || (user?.organizationRole?.priority ?? 0) >= 50;
  const currentUserId = user?.id || (user as any)?._id;
  const orgPriority = (user as any)?.organizationRole?.priority || 0;
  const [features, setFeatures] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchPending = async () => {
    try {
      setLoading(true);
      if (projectId) {
        const res = await featureAPI.getByProject(projectId);
        const all = res.data?.data || res.data || [];
        setFeatures(all.filter((f: any) => f.approvalStatus === 'pending_approval'));
      } else {
        const res = await featureAPI.getPendingApprovals();
        setFeatures(res.data?.data || res.data || []);
      }
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, [projectId]);

  const handleApprove = async (featureId: string) => {
    setActionLoading(featureId);
    try {
      await featureAPI.approve(featureId);
      await fetchPending();
    } catch {
      /* error */
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (featureId: string) => {
    if (!rejectReason.trim()) return;
    setActionLoading(featureId);
    try {
      await featureAPI.reject(featureId, { reason: rejectReason });
      setRejectingId(null);
      setRejectReason('');
      await fetchPending();
    } catch {
      /* error */
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (features.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-8">
        <div className="w-20 h-20 rounded-2xl bg-green-50 flex items-center justify-center mb-6">
          <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No pending approvals</h2>
        <p className="text-sm text-gray-500 max-w-sm">
          All feature submissions have been reviewed. New submissions will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          Pending Approvals
          <span className="ml-2 text-sm font-normal text-gray-500">({features.length})</span>
        </h2>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 font-medium text-gray-600">Feature</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Project</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Submitter</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Priority</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Points</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Submitted</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {features.map((feature: any) => (
              <tr key={feature._id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <span className="font-medium text-gray-900">{feature.title || feature.name}</span>
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {feature.projectName || feature.project?.name || '-'}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {feature.owner?.name || feature.createdByName || '-'}
                </td>
                <td className="px-4 py-3">
                  {feature.priority && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${priorityColor[feature.priority] || 'bg-gray-100 text-gray-600'}`}>
                      {feature.priority}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {feature.storyPoints != null ? feature.storyPoints : '-'}
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">
                  {feature.submittedAt
                    ? new Date(feature.submittedAt).toLocaleDateString()
                    : feature.createdAt
                    ? new Date(feature.createdAt).toLocaleDateString()
                    : '-'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {!canApprove ? (
                      <span className="text-xs text-gray-400">Awaiting admin review</span>
                    ) : (feature.submittedBy && currentUserId && String(typeof feature.submittedBy === 'object' ? (feature.submittedBy as any)._id || (feature.submittedBy as any).userId : feature.submittedBy) === String(currentUserId) && orgPriority < 50) ? (
                      <span className="text-xs text-gray-400 italic">You cannot approve your own submission</span>
                    ) : rejectingId === feature._id ? (
                      <div className="flex items-center gap-2">
                        <input
                          value={rejectReason}
                          onChange={e => setRejectReason(e.target.value)}
                          placeholder="Reason for rejection"
                          className="border border-gray-300 rounded-lg px-2 py-1 text-xs w-40 focus:outline-none focus:ring-1 focus:ring-red-400"
                          autoFocus
                        />
                        <button
                          onClick={() => handleReject(feature._id)}
                          disabled={!rejectReason.trim() || actionLoading === feature._id}
                          className="bg-red-600 text-white px-2 py-1 rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => { setRejectingId(null); setRejectReason(''); }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => handleApprove(feature._id)}
                          disabled={actionLoading === feature._id}
                          className="bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded-lg text-xs font-medium hover:bg-green-100 disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => setRejectingId(feature._id)}
                          disabled={actionLoading === feature._id}
                          className="bg-red-50 text-red-700 border border-red-200 px-3 py-1 rounded-lg text-xs font-medium hover:bg-red-100 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FeatureApprovalQueue;
